/*
 * Copyright (c) 2026 Macrofocus GmbH. All Rights Reserved.
 */
package com.macrofocus.high_d.parallelcoordinates

import org.mkui.canvas.CPCanvas
import org.mkui.canvas.MouseEvent
import org.mkui.canvas.MouseListener
import org.mkui.canvas.MouseMotionListener
import org.mkui.canvas.MouseWheelEvent
import org.mkui.canvas.MouseWheelListener
import org.mkui.color.MkColor
import org.mkui.color.colorOf
import org.mkui.geom.Point2D
import org.mkui.geom.Rectangle
import org.mkui.geom.Rectangle2D
import org.mkui.graphics.AbstractIDrawing
import org.mkui.graphics.IGraphics
import kotlin.math.max
import kotlin.math.min

/**
 * A horizontal scroll bar over a content [contentWidth] wide seen through a viewport [viewportWidth] wide, as the
 * scroll bar of the JScrollPane of SwingParallelCoordinatesView. Dragging the thumb, pressing the track or turning the
 * mouse wheel over it changes [value], the left of the viewport in the content, and calls [onScroll].
 */
class HorizontalScrollBarView(private val onScroll: (Double) -> Unit) : CPCanvas() {
    var contentWidth: Double = 0.0
        private set
    var viewportWidth: Double = 0.0
        private set
    var value: Double = 0.0
        private set

    /** Sets the extents of the content and of the viewport, keeping [value] within the content. */
    fun setExtents(contentWidth: Double, viewportWidth: Double) {
        this.contentWidth = contentWidth
        this.viewportWidth = viewportWidth
        value = clamp(value)
        drawing.changed()
    }

    private fun clamp(v: Double): Double = max(0.0, min(v, contentWidth - viewportWidth))

    private fun scrollTo(v: Double) {
        val clamped = clamp(v)
        if (clamped != value) {
            value = clamped
            drawing.changed()
            onScroll(value)
        }
    }

    /** The thumb in the coordinates of the scroll bar. */
    private fun thumb(width: Double): Rectangle2D {
        if (contentWidth <= 0) {
            return Rectangle2D.Double(0.0, 0.0, width, HEIGHT)
        }
        val w = max(MIN_THUMB, width * viewportWidth / contentWidth)
        val range = contentWidth - viewportWidth
        val x = if (range > 0) (width - w) * value / range else 0.0
        return Rectangle2D.Double(x, 2.0, w, HEIGHT - 4)
    }

    private val drawing = object : AbstractIDrawing() {
        override fun draw(g: IGraphics, point: Point2D?, width: Double, height: Double, clipBounds: Rectangle) {
            if (width <= 0 || height <= 0) {
                return
            }
            g.setColor(TRACK)
            g.fillRectangle(0, 0, width.toInt(), height.toInt())
            g.setColor(if (dragging) THUMB_DRAGGED else THUMB)
            val t = thumb(width)
            g.fillRectangle(t.x.toInt(), t.y.toInt(), t.width.toInt(), t.height.toInt())
        }

        fun changed() {
            notifyIDrawingChanged()
        }
    }

    private var dragging = false
    private var pressX = 0
    private var pressValue = 0.0

    private val mouseListener = object : MouseListener, MouseMotionListener {
        override fun mousePressed(e: MouseEvent) {
            e.stopPropagation()
            val width = getWidth()
            val t = thumb(width)
            if (e.x < t.x || e.x > t.maxX) {
                // A page to the left or to the right
                scrollTo(value + if (e.x < t.x) -viewportWidth else viewportWidth)
            } else {
                dragging = true
                pressX = e.x
                pressValue = value
                drawing.changed()
            }
        }

        override fun mouseDragged(e: MouseEvent) {
            e.stopPropagation()
            if (dragging) {
                val width = getWidth()
                val t = thumb(width)
                val track = width - t.width
                if (track > 0) {
                    scrollTo(pressValue + (e.x - pressX) * (contentWidth - viewportWidth) / track)
                }
            }
        }

        override fun mouseReleased(e: MouseEvent) {
            e.stopPropagation()
            dragging = false
            drawing.changed()
        }

        override fun mouseClicked(e: MouseEvent) {
            e.stopPropagation()
        }

        override fun mouseEntered(e: MouseEvent) {
        }

        override fun mouseExited(e: MouseEvent) {
        }

        override fun mouseMoved(e: MouseEvent) {
            e.stopPropagation()
        }
    }

    init {
        addLayer(drawing)
        addMouseListener(mouseListener)
        addMouseMotionListener(mouseListener)
        addMouseWheelListener(object : MouseWheelListener {
            override fun mouseWheelMoved(event: MouseWheelEvent) {
                scrollTo(value + event.wheelRotation * WHEEL_STEP)
            }
        })
    }

    companion object {
        const val HEIGHT = 12.0

        private const val MIN_THUMB = 24.0
        private const val WHEEL_STEP = 40.0

        private val TRACK: MkColor = colorOf(240, 240, 240)
        private val THUMB: MkColor = colorOf(190, 190, 190)
        private val THUMB_DRAGGED: MkColor = colorOf(150, 150, 150)
    }
}
